import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { SynopsisCard } from "@/components/JobDetails/SynopsisCard";
import { BookDetailsCard } from "@/components/JobDetails/BookDetailsCard";
import { TropesCard } from "@/components/JobDetails/TropesCard";
import { CharacterCard } from "@/components/JobDetails/CharacterCard";
import { WorldAndSettingCard } from "@/components/JobDetails/WorldAndSettingCard";
import { hasOverviewCards } from "@/utils/hasOverviewCards";
import { useJobDetail } from "@/contexts/JobDetailContext";

export const Route = createFileRoute("/jobs/$jobId/overview")({
  component: OverviewView,
});

export function OverviewView() {
  const { summaryData, isLoading, job } = useJobDetail();
  const { markedForDeletion } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const { t } = useTranslation("jobDetails");
  const hasRedirectedEmpty = useRef(false);

  const showOverview = hasOverviewCards(summaryData);

  useEffect(() => {
    if (isLoading) return;
    if (showOverview) return;
    if (!job) return;
    if (hasRedirectedEmpty.current) return;
    hasRedirectedEmpty.current = true;

    navigate({
      to: "/jobs/$jobId/summary",
      params: { jobId: job.jobId },
      search: { markedForDeletion },
      replace: true,
    });
  }, [isLoading, showOverview, job, markedForDeletion, navigate]);

  if (isLoading) {
    return (
      <div className="pb-8">
        <div className="bg-white rounded-[1.5rem] shadow p-6">
          <p className="text-muted-foreground">{t("overview.loading")}</p>
        </div>
      </div>
    );
  }

  if (!showOverview || !summaryData) {
    return null;
  }

  return (
    <div className="pb-8 space-y-6">
      {summaryData.manuscript_word_count != null &&
        summaryData.reading_time_minutes != null && (
          <BookDetailsCard
            wordCount={summaryData.manuscript_word_count}
            readingTimeMinutes={summaryData.reading_time_minutes}
            genres={summaryData.genres}
            worldSetting={summaryData.world_setting}
          />
        )}
      <TropesCard tropes={summaryData.tropes} />
      <SynopsisCard synopsis={summaryData.synopsis} />
      <CharacterCard characters={summaryData.characters} />
      <WorldAndSettingCard worldSetting={summaryData.world_setting} />
      <p
        data-testid="overview-more-features"
        className="text-center text-secondary-400 max-w-xl mx-auto leading-relaxed font-light text-sm py-6"
      >
        {t("overview.moreFeaturesComingSoon")}
      </p>
    </div>
  );
}
